import axios from 'axios';
import { Dispatch } from 'redux';
import { setNotification } from '@/app/lib/reducers/notificationsReducer';
import { BookingItem } from "@/data/types"; // Adjust the import if needed to match your actual file structure
import { RootState } from '../store'; // Assuming you have a RootState type for your Redux store
import api from './api';

// Action Types
const GET_BOOKINGS_REQUEST = 'GET_BOOKINGS_REQUEST';
const GET_BOOKINGS_SUCCESS = 'GET_BOOKINGS_SUCCESS';
const GET_BOOKINGS_FAILURE = 'GET_BOOKINGS_FAILURE';
const CANCEL_BOOKING_REQUEST = 'CANCEL_BOOKING_REQUEST';
const CANCEL_BOOKING_SUCCESS = 'CANCEL_BOOKING_SUCCESS';
const CANCEL_BOOKING_FAILURE = 'CANCEL_BOOKING_FAILURE';
const UPDATE_BOOKING_REQUEST = 'UPDATE_BOOKING_REQUEST';
const UPDATE_BOOKING_SUCCESS = 'UPDATE_BOOKING_SUCCESS';
const UPDATE_BOOKING_FAILURE = 'UPDATE_BOOKING_FAILURE';

// Action Interfaces
interface GetBookingsRequestAction {
  type: typeof GET_BOOKINGS_REQUEST;
}

interface GetBookingsSuccessAction {
  type: typeof GET_BOOKINGS_SUCCESS;
  payload: BookingItem[];  // Updated to use BookingItem[]
}

interface GetBookingsFailureAction {
  type: typeof GET_BOOKINGS_FAILURE;
  error: string;
}

type BookingsActions =
  | GetBookingsRequestAction
  | GetBookingsSuccessAction
  | GetBookingsFailureAction
  | CancelBookingRequestAction
  | CancelBookingSuccessAction
  | CancelBookingFailureAction
  | UpdateBookingRequestAction
  | UpdateBookingSuccessAction
  | UpdateBookingFailureAction;

interface CancelBookingRequestAction {
  type: typeof CANCEL_BOOKING_REQUEST;
}

interface CancelBookingSuccessAction {
  type: typeof CANCEL_BOOKING_SUCCESS;
  payload: any;
}

interface CancelBookingFailureAction {
  type: typeof CANCEL_BOOKING_FAILURE;
  error: string;
}

interface UpdateBookingRequestAction {
  type: typeof UPDATE_BOOKING_REQUEST;
}

interface UpdateBookingSuccessAction {
  type: typeof UPDATE_BOOKING_SUCCESS;
  payload: any;
}

interface UpdateBookingFailureAction {
  type: typeof UPDATE_BOOKING_FAILURE;
  error: string;
}

// State Type
interface BookingsState {
  items: BookingItem[];
  loading: boolean;
  error: string | null;
}

// Initial State
const initialState: BookingsState = {
  items: [],
  loading: false,
  error: null,
};

// Action Creators
export const getMyBookings = (year?: string, month?: string) => 
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch({ type: GET_BOOKINGS_REQUEST });

    const userString = localStorage.getItem("user");
    let accessToken = "";

    if (userString) {
      const user = JSON.parse(userString);
      accessToken = user.access_token;
    }

    try {
      const response = await api.get(`/api/me/bookings`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          ...(year && { year }),
          ...(month && { month }),
        },
      });

      dispatch({ type: GET_BOOKINGS_SUCCESS, payload: response.data });
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      dispatch({ type: GET_BOOKINGS_FAILURE, error: errorMessage });
    }
};

export const cancelBooking =
(cartReference: string, reason: string) =>
async (dispatch: Dispatch) => {
  dispatch({ type: CANCEL_BOOKING_REQUEST });

  try {
    const response = await api.put(`/api/me/bookings/${cartReference}/cancel`, {
      reason,
    });

    dispatch({
      type: CANCEL_BOOKING_SUCCESS,
      payload: response.data?.data,
    });

    if (response.status === 200) {
      const notificationMessage = `Booking ${cartReference} Cancelled successfully`;
      dispatch(
        setNotification({
          type: 'success',
          message: notificationMessage,
        })
      );
    } else {
      const notificationMessage = "Booking cancellation failed";

      dispatch(
        setNotification({
          type: 'error',
          message: notificationMessage,
        })
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    dispatch({ type: CANCEL_BOOKING_FAILURE, error: errorMessage });

    dispatch(
      setNotification({
        type: 'error',
        message: errorMessage,
      })
    );
  }
};

export const updateBooking =
(cartReference: string, booking: any) =>
async (dispatch: Dispatch) => {
  dispatch({ type: UPDATE_BOOKING_REQUEST });

  try {
    const response = await api.put(`/api/me/bookings/${cartReference}/update`, booking);

    dispatch({
      type: UPDATE_BOOKING_SUCCESS,
      payload: response.data?.data,
    });

    if (response.status === 200) {
      const notificationMessage = `Booking ${cartReference} updated successfully`;
      dispatch(
        setNotification({
          type: 'success',
          message: notificationMessage,
        })
      );
    } else {
      const notificationMessage = "Booking updation failed";

      dispatch(
        setNotification({
          type: 'error',
          message: notificationMessage,
        })
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (axios.isAxiosError(error)) {
      errorMessage = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }

    dispatch({ type: UPDATE_BOOKING_FAILURE, error: errorMessage });

    dispatch(
      setNotification({
        type: 'error',
        message: errorMessage,
      })
    );
  }
};

// Reducer
const bookingsReducer = (state: BookingsState = initialState, action: BookingsActions): BookingsState => {
    switch (action.type) {
      case GET_BOOKINGS_REQUEST:
        return {
          ...state,
          loading: true,
          error: null,
        };
      case CANCEL_BOOKING_REQUEST:
        return {
          ...state,
          loading: true,
          error: null,
        };
      case UPDATE_BOOKING_REQUEST:
        return {
          ...state,
          loading: true,
          error: null,
        };
      case GET_BOOKINGS_SUCCESS:
        return {
          ...state,
          loading: false,
          items: action.payload,
        };
      case CANCEL_BOOKING_SUCCESS:
        return {
          ...state,
          loading: false,
          items: state.items.map(booking =>
            booking.cartReference === action.payload.cartReference
              ? {
                  ...booking,
                  visitStatus: action.payload.visitStatus
                }
              : booking
          ),
        };
      case UPDATE_BOOKING_SUCCESS:
        return {
          ...state,
          loading: false,
          items: state.items.map(booking =>
            booking.cartReference === action.payload.cartReference
              ? {
                  ...booking,
                  meetingPoint: action.payload?.meetingPoint,
                  date: action.payload?.date,
                  time: action.payload?.time
                }
              : booking
          ),
        };
      case GET_BOOKINGS_FAILURE:
        return {
          ...state,
          loading: false,
          error: action.error,
        };
      case CANCEL_BOOKING_FAILURE:
        return {
          ...state,
          loading: false,
          error: action.error,
        };
      case UPDATE_BOOKING_FAILURE:
        return {
          ...state,
          loading: false,
          error: action.error,
        };
      default:
        return state;
    }
};
  
export default bookingsReducer;
